import { slippageScenarios } from '../data';
import { useRef, useState, useEffect } from 'react';

const PIP_VALUE = 10; // $ per pip per standard lot
const AVG_SPREAD_PIPS = 1.2;

const meta = [
  { key: 'secondary', bar: 'bg-secondary-500', dot: 'bg-secondary-500', text: 'text-secondary-700' },
  { key: 'accent', bar: 'bg-accent-500', dot: 'bg-accent-500', text: 'text-accent-700' },
  { key: 'primary', bar: 'bg-primary-500', dot: 'bg-primary-500', text: 'text-primary-700' },
];

const rows = slippageScenarios.map((s, i) => {
  const monthlySlippage = s.tradesPerMonth * s.lotSize * s.slippageRate * PIP_VALUE;
  const monthlySpread = s.tradesPerMonth * s.lotSize * AVG_SPREAD_PIPS * PIP_VALUE;
  const monthlyTotal = monthlySlippage + monthlySpread;
  return {
    label: s.label,
    description: s.description,
    tradesPerMonth: s.tradesPerMonth,
    lotSize: s.lotSize,
    slippageRate: s.slippageRate,
    monthlySlippage,
    monthlySpread,
    monthlyTotal,
    annualSlippage: monthlySlippage * 12,
    annualTotal: monthlyTotal * 12,
    ...meta[i],
  };
});

const maxAnnualSlippage = Math.max(...rows.map((r) => r.annualSlippage));
const calm = rows[0];
const news = rows[1];
const ratio = (news.annualSlippage / Math.max(calm.annualSlippage, 1)).toFixed(1);

const fmt = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function SlippageCostSection() {
  const chartRef = useRef<HTMLDivElement>(null);
  const [barsInView, setBarsInView] = useState(false);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBarsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Editorial header */}
        <div className="max-w-[720px] mb-10 md:mb-12">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            How much slippage costs in real dollars
          </h2>
          <p className="text-[17px] leading-[1.65] text-foreground-600">
            Three traders, same broker, three trading conditions. Trade volume alone doesn&apos;t
            predict the bill — the trader making just eight trades a month carries the heaviest
            slippage burden.
          </p>
        </div>

        {/* Lead statistic + chart, editorial two-column */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pt-8 border-t-2 border-foreground-950">
          {/* Lead pull-stat */}
          <div className="lg:col-span-4">
            <div className="text-[13px] font-semibold text-foreground-500 uppercase tracking-[0.12em] mb-3">
              The Headline
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-[72px] md:text-[84px] font-[750] text-accent-600 leading-none tracking-tight tabular-nums">
                {ratio}×
              </span>
            </div>
            <p className="text-[15px] leading-[1.7] text-foreground-700 max-w-[320px]">
              A trader placing <strong className="font-semibold text-foreground-950">8 news trades</strong>{' '}
              per month pays roughly <strong className="font-semibold text-accent-600">{ratio}×</strong>{' '}
              more annual slippage than one placing{' '}
              <strong className="font-semibold text-foreground-950">100 calm-market trades</strong> —
              because pips lost per trade matter far more than trade count.
            </p>
          </div>

          {/* Horizontal bar chart */}
          <div className="lg:col-span-8" ref={chartRef}>
            <div className="flex items-center justify-between mb-6">
              <span className="text-[13px] font-semibold text-foreground-500 uppercase tracking-[0.12em]">
                Annual Slippage Cost
              </span>
              <span className="text-[12px] text-foreground-400">USD · per standard-lot basis</span>
            </div>

            <div className="space-y-6">
              {rows.map((r, i) => {
                const pct = Math.round((r.annualSlippage / maxAnnualSlippage) * 100);
                return (
                  <div key={r.label}>
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.dot}`} />
                        <span className="text-[15px] font-semibold text-foreground-900">{r.label}</span>
                        <span className="text-[12px] text-foreground-400 hidden sm:inline">
                          {r.tradesPerMonth} trades · {r.slippageRate} pip avg
                        </span>
                      </div>
                      <span className="text-[18px] font-bold text-foreground-950 tabular-nums">
                        ${fmt(r.annualSlippage)}
                      </span>
                    </div>
                    <div className="w-full h-3 bg-background-100 overflow-hidden rounded-sm">
                      <div
                        className={`h-full ${r.bar} transition-[width] duration-[1000ms] ease-out rounded-sm`}
                        style={{
                          width: barsInView ? `${pct}%` : '0%',
                          transitionDelay: barsInView ? `${i * 180}ms` : '0ms',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Detailed comparison table — editorial style */}
        <div className="mt-14 md:mt-16">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <h3 className="text-[15px] font-bold text-foreground-950 uppercase tracking-[0.1em]">
              Full Cost Breakdown
            </h3>
            <span className="text-[12px] text-foreground-400">Figures rounded to the nearest dollar</span>
          </div>

          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
            <table className="w-full min-w-[620px] border-collapse">
              <thead>
                <tr className="border-b-2 border-foreground-950">
                  <th className="text-left py-3 pr-4 text-[12px] font-semibold text-foreground-500 uppercase tracking-[0.1em] w-[34%]">
                    Metric
                  </th>
                  {rows.map((r) => (
                    <th key={r.label} className="text-right py-3 pl-4">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`w-2 h-2 rounded-full ${r.dot}`} />
                        <span className="text-[14px] font-bold text-foreground-950">{r.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-background-200">
                  <td className="py-3 pr-4 text-[14px] text-foreground-600">Trades per month</td>
                  {rows.map((r) => (
                    <td key={r.label} className="py-3 pl-4 text-right text-[14px] font-semibold text-foreground-900 tabular-nums">
                      {r.tradesPerMonth}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-3 pr-4 text-[14px] text-foreground-600">Average lot size</td>
                  {rows.map((r) => (
                    <td key={r.label} className="py-3 pl-4 text-right text-[14px] font-semibold text-foreground-900 tabular-nums">
                      {r.lotSize.toFixed(1)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-3 pr-4 text-[14px] text-foreground-600">Slippage per trade</td>
                  {rows.map((r) => (
                    <td key={r.label} className="py-3 pl-4 text-right text-[14px] font-semibold text-foreground-900 tabular-nums">
                      {r.slippageRate.toFixed(1)} pip
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-3 pr-4 text-[14px] text-foreground-600">Monthly slippage</td>
                  {rows.map((r) => (
                    <td key={r.label} className="py-3 pl-4 text-right text-[14px] font-semibold text-accent-600 tabular-nums">
                      ${fmt(r.monthlySlippage)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-3 pr-4 text-[14px] text-foreground-600">Monthly spread</td>
                  {rows.map((r) => (
                    <td key={r.label} className="py-3 pl-4 text-right text-[14px] font-semibold text-foreground-700 tabular-nums">
                      ${fmt(r.monthlySpread)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-background-200">
                  <td className="py-3 pr-4 text-[14px] font-semibold text-foreground-800">Annual slippage</td>
                  {rows.map((r) => (
                    <td key={r.label} className="py-3 pl-4 text-right text-[16px] font-bold text-foreground-950 tabular-nums">
                      ${fmt(r.annualSlippage)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="pt-4 pr-4 text-[14px] font-semibold text-foreground-500">
                    Annual total <span className="font-normal">(incl. spread)</span>
                  </td>
                  {rows.map((r) => (
                    <td key={r.label} className="pt-4 pl-4 text-right text-[14px] font-semibold text-foreground-500 tabular-nums">
                      ${fmt(r.annualTotal)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Methodology footnote */}
          <div className="mt-6 pt-4 border-t border-background-200 flex items-start gap-2.5 max-w-[760px]">
            <span className="text-[13px] font-bold text-foreground-400 leading-relaxed">Note</span>
            <p className="text-[13px] leading-[1.65] text-foreground-500">
              Based on ${PIP_VALUE} per pip per standard lot and a {AVG_SPREAD_PIPS}-pip average spread.
              Calm-market traders place far more orders, so their total spread cost is higher — yet news
              traders still generate the most <em>slippage</em>, because each trade slips several pips
              during volatile releases. Illustrative figures for comparison only.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}